import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  Shield, 
  Home, 
  Database, 
  Users, 
  Building2, 
  ChevronRight, 
  ChevronLeft,
  CheckCircle2,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Partner Types Configuration
const agentTypes = [
  {
    id: 'software_provider',
    title: 'Software Provider',
    description: 'CRM systems, quoting tools, and complimentary software solutions',
    icon: Database,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  },
  {
    id: 'marketing_professional',
    title: 'Marketing Professional',
    description: 'Marketing services, lead generation, and website design',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  },
  {
    id: 'developer',
    title: 'Developer',
    description: 'Software development and custom integrations',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  },
  {
    id: 'imo_bga',
    title: 'IMO / BGA',
    description: 'IMOs and BGAs offering contracting services',
    icon: Building2,
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  },
  {
    id: 'carrier_bga',
    title: 'Add a Product',
    description: 'Submit a new carrier or product for our quoting system',
    icon: Home,
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  },
  {
    id: 'other',
    title: 'Other Partnership',
    description: 'Other partnership opportunities not listed above',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  },
];

// Years of Experience Options
const experienceOptions = [
  { value: 'less_than_1', label: 'Less than 1 year' },
  { value: '1_3', label: '1-3 years' },
  { value: '3_5', label: '3-5 years' },
  { value: '5_10', label: '5-10 years' },
  { value: '10_plus', label: '10+ years' },
];

// Category-specific questions configuration
const categoryQuestions = {
  software_provider: {
    title: 'Tell Us About Your Software',
    questions: [
      { id: 'software_type', label: 'What type of software do you offer?', type: 'select', 
        options: [
          { value: 'crm', label: 'CRM System' },
          { value: 'quoting', label: 'Quoting Tools' },
          { value: 'agency_management', label: 'Agency Management System' },
          { value: 'lead_management', label: 'Lead Management' },
          { value: 'compliance', label: 'Compliance Software' },
          { value: 'other', label: 'Other Software' },
        ]
      },
      { id: 'integration_experience', label: 'Do you currently integrate with other insurance platforms?', type: 'select',
        options: [
          { value: 'yes_multiple', label: 'Yes, multiple platforms' },
          { value: 'yes_few', label: 'Yes, a few' },
          { value: 'no_interested', label: 'No, but interested' },
          { value: 'no', label: 'No' },
        ]
      },
      { id: 'user_base', label: 'How many insurance agents/agencies use your software?', type: 'select',
        options: [
          { value: 'under_100', label: 'Under 100' },
          { value: '100_500', label: '100-500' },
          { value: '500_1000', label: '500-1,000' },
          { value: '1000_plus', label: '1,000+' },
        ]
      },
    ]
  },
  marketing_professional: {
    title: 'Tell Us About Your Marketing Services',
    questions: [
      { id: 'marketing_services', label: 'What marketing services do you specialize in?', type: 'select',
        options: [
          { value: 'lead_generation', label: 'Lead Generation' },
          { value: 'website_design', label: 'Website Design & Development' },
          { value: 'seo_sem', label: 'SEO / SEM' },
          { value: 'social_media', label: 'Social Media Marketing' },
          { value: 'content_marketing', label: 'Content Marketing' },
          { value: 'full_service', label: 'Full Service Agency' },
        ]
      },
      { id: 'insurance_experience', label: 'Do you have experience in the insurance industry?', type: 'select',
        options: [
          { value: 'yes_extensive', label: 'Yes, extensive experience' },
          { value: 'yes_some', label: 'Yes, some experience' },
          { value: 'no_interested', label: 'No, but interested in this niche' },
        ]
      },
      { id: 'client_size', label: 'What is your typical client size?', type: 'select',
        options: [
          { value: 'individual_agents', label: 'Individual Agents' },
          { value: 'small_agencies', label: 'Small Agencies (1-10 agents)' },
          { value: 'mid_agencies', label: 'Mid-size Agencies (10-50 agents)' },
          { value: 'large_agencies', label: 'Large Agencies (50+ agents)' },
          { value: 'all_sizes', label: 'All Sizes' },
        ]
      },
    ]
  },
  developer: {
    title: 'Tell Us About Your Development Services',
    questions: [
      { id: 'tech_stack', label: 'What technologies do you specialize in?', type: 'input', placeholder: 'e.g., React, Python, Node.js, APIs' },
      { id: 'insurance_api_experience', label: 'Do you have experience with insurance or fintech APIs?', type: 'select',
        options: [
          { value: 'yes_extensive', label: 'Yes, extensive experience' },
          { value: 'yes_some', label: 'Yes, some experience' },
          { value: 'no_interested', label: 'No, but interested' },
        ]
      },
      { id: 'integration_interest', label: 'What type of development work interests you?', type: 'select',
        options: [
          { value: 'api_integration', label: 'API Integrations' },
          { value: 'custom_development', label: 'Custom Development' },
          { value: 'white_label', label: 'White-Label Solutions' },
          { value: 'consulting', label: 'Technical Consulting' },
          { value: 'all', label: 'All of the Above' },
        ]
      },
    ]
  },
  imo_bga: {
    title: 'Tell Us About Your Organization',
    questions: [
      { id: 'agent_count', label: 'How many agents do you currently support?', type: 'select',
        options: [
          { value: 'under_50', label: 'Under 50' },
          { value: '50_200', label: '50-200' },
          { value: '200_500', label: '200-500' },
          { value: '500_1000', label: '500-1,000' },
          { value: '1000_plus', label: '1,000+' },
        ]
      },
      { id: 'carrier_contracts', label: 'What carriers do you have contracts with?', type: 'input', placeholder: 'e.g., Protective, Lincoln, Pacific Life' },
      { id: 'regions_served', label: 'What regions do you serve?', type: 'select',
        options: [
          { value: 'national', label: 'National' },
          { value: 'multi_state', label: 'Multi-State Regional' },
          { value: 'single_state', label: 'Single State' },
        ]
      },
    ]
  },
  carrier_bga: {
    title: 'Tell Us About Your Product',
    questions: [
      { id: 'product_type', label: 'What type of product is it?', type: 'select',
        options: [
          { value: 'life_insurance', label: 'Life Insurance' },
          { value: 'annuity', label: 'Annuity' },
          { value: 'disability', label: 'Disability Insurance' },
          { value: 'ltc', label: 'Long-Term Care' },
          { value: 'other', label: 'Other' },
        ]
      },
      { id: 'life_insurance_type', label: 'What type of life insurance product is it?', type: 'select',
        options: [
          { value: 'term', label: 'Term Life' },
          { value: 'whole_life', label: 'Whole Life' },
          { value: 'universal_life', label: 'Universal Life (UL)' },
          { value: 'indexed_ul', label: 'Indexed Universal Life (IUL)' },
          { value: 'variable_ul', label: 'Variable Universal Life (VUL)' },
          { value: 'final_expense', label: 'Final Expense / Burial' },
          { value: 'guaranteed_issue', label: 'Guaranteed Issue' },
          { value: 'multiple', label: 'Multiple Product Types' },
          { value: 'not_applicable', label: 'Not Applicable' },
        ]
      },
      { id: 'can_provide_rates', label: 'Can you provide the rates to add to our system?', type: 'select',
        options: [
          { value: 'yes_ready', label: 'Yes, we have rates ready to share' },
          { value: 'yes_need_time', label: 'Yes, but we need some time to prepare' },
          { value: 'need_discussion', label: 'We need to discuss requirements first' },
          { value: 'not_sure', label: 'Not sure' },
        ]
      },
      { id: 'api_access', label: 'Do you have API access to your quoting/application systems?', type: 'select',
        options: [
          { value: 'yes_full', label: 'Yes, full API access' },
          { value: 'yes_limited', label: 'Yes, limited API access' },
          { value: 'in_development', label: 'In Development' },
          { value: 'no', label: 'No' },
        ]
      },
    ]
  },
  other: {
    title: 'Tell Us About Your Partnership Idea',
    questions: [
      { id: 'business_description', label: 'Please describe your business', type: 'textarea', placeholder: 'Tell us about your company and what you do...' },
      { id: 'partnership_idea', label: 'What type of partnership are you interested in?', type: 'textarea', placeholder: 'Describe your partnership idea...' },
      { id: 'value_proposition', label: 'How do you think we can work together?', type: 'textarea', placeholder: 'What value can you bring to Quoteplicity customers?' },
    ]
  },
};

// Validation Schemas
const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string()
    .min(10, 'Phone number must be at least 10 digits')
    .regex(/^[\d\s\-\(\)\+]+$/, 'Please enter a valid phone number')
    .transform(val => val.replace(/\D/g, ''))
    .refine(val => val.length >= 10, 'Phone number must be at least 10 digits'),
});

const businessSchema = z.object({
  company_name: z.string().min(2, 'Company name must be at least 2 characters'),
  website: z.string()
    .optional()
    .refine(val => !val || val === '' || /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(val), 
      'Please enter a valid website URL (e.g., https://example.com)'),
  question_1: z.string().optional(),
  question_2: z.string().optional(),
  question_3: z.string().optional(),
  question_4: z.string().optional(),
  comments: z.string().optional(),
});

// Phone number formatter
const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  if (phoneNumber.length < 4) return phoneNumber;
  if (phoneNumber.length < 7) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

// Animation Variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const cardVariants = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  hover: { scale: 1.02, y: -4 },
};

export const PartnerWidget = ({ isEmbed = false }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedAgentType, setSelectedAgentType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [contactData, setContactData] = useState({ name: '', email: '', phone: '' });
  const [businessData, setBusinessData] = useState({
    company_name: '',
    website: '',
    question_1: '',
    question_2: '',
    question_3: '',
    comments: '',
  });

  const totalSteps = 4;
  const progress = (currentStep / totalSteps) * 100;

  // Helper to publish height to parent iframe
  const publishHeight = useRef(() => {
    if (!isEmbed) return;
    requestAnimationFrame(() => {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ type: 'resize', height }, '*');
    });
  }).current;

  // Helper to notify parent to scroll to top
  const scrollParentToTop = useRef(() => {
    if (!isEmbed) return;
    window.parent.postMessage({ type: 'scrollToTop' }, '*');
  }).current;

  // Set up iframe height communication with parent (only in embed mode)
  useEffect(() => {
    if (!isEmbed) return;

    // Suppress body scrollbar and set transparent background for iframe embedding
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    document.documentElement.style.overflow = 'visible';
    document.documentElement.style.background = 'transparent';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'visible';
    document.body.style.background = 'transparent';

    // Publish initial height after DOM has painted
    const handleLoad = () => requestAnimationFrame(publishHeight);
    window.addEventListener('load', handleLoad);
    
    // Also publish immediately in case already loaded
    requestAnimationFrame(publishHeight);

    // Observe body resize (step transitions, validation messages, etc.)
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => requestAnimationFrame(publishHeight));
      resizeObserver.observe(document.documentElement);
    }

    // Also publish on window resize for safety
    window.addEventListener('resize', publishHeight);

    return () => {
      window.removeEventListener('load', handleLoad);
      window.removeEventListener('resize', publishHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isEmbed, publishHeight]);

  // Publish height whenever step or submission state changes
  useEffect(() => {
    if (!isEmbed) return;
    const timeout = setTimeout(publishHeight, 100);
    return () => clearTimeout(timeout);
  }, [currentStep, submissionSuccess, selectedAgentType, isEmbed, publishHeight]);

  // Contact Form with persisted values
  const contactForm = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: contactData,
    values: contactData,
  });

  // Business Form with persisted values
  const businessForm = useForm({
    resolver: zodResolver(businessSchema),
    defaultValues: businessData,
    values: businessData,
  });

  const handleAgentTypeSelect = (agentType) => {
    setSelectedAgentType(agentType);
  };

  const handleContactSubmit = (data) => {
    setContactData(data);
    setCurrentStep(3);
    if (isEmbed) {
      scrollParentToTop();
      requestAnimationFrame(publishHeight);
    }
  };

  const handleBusinessSubmit = async (data) => {
    setIsSubmitting(true);
    setBusinessData(data);
    
    // Get question labels for the selected category
    const categoryConfig = categoryQuestions[selectedAgentType.id];
    const formattedResponses = {};
    
    if (categoryConfig) {
      categoryConfig.questions.forEach((q, index) => {
        const answer = data[`question_${index + 1}`];
        if (answer) {
          formattedResponses[q.id] = answer;
        }
      });
    }
    
    const submissionData = {
      agent_type: selectedAgentType.id,
      agent_type_label: selectedAgentType.title,
      ...contactData,
      company_name: data.company_name,
      website: data.website,
      category_responses: formattedResponses,
      comments: data.comments,
    };

    try {
      await axios.post(`${API}/partners`, submissionData);
      setSubmissionSuccess(true);
      setCurrentStep(4);
      if (isEmbed) {
        scrollParentToTop();
        requestAnimationFrame(publishHeight);
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    // Save current form data before going back
    if (currentStep === 3) {
      setBusinessData(businessForm.getValues());
    } else if (currentStep === 2) {
      setContactData(contactForm.getValues());
    }
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      if (isEmbed) {
        scrollParentToTop();
        requestAnimationFrame(publishHeight);
      }
    }
  };

  const goToStep2 = () => {
    if (selectedAgentType) {
      setCurrentStep(2);
      if (isEmbed) {
        scrollParentToTop();
        requestAnimationFrame(publishHeight);
      }
    }
  };

  // Reference to the widget container for height measurement
  const widgetRef = useRef(null);

  // Scroll to top of widget when step changes (non-embed mode)
  useEffect(() => {
    if (!isEmbed && widgetRef.current) {
      widgetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentStep, isEmbed]);

  return (
    <div ref={widgetRef} className={`widget-container py-8 px-4 ${isEmbed ? 'bg-transparent' : ''}`} data-testid="partner-widget">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Become a Partner
          </h1>
          <p className="text-slate-600 text-base md:text-lg">
            Join Quoteplicity and grow your insurance business
          </p>
        </motion.div>

        {/* Progress Bar */}
        <div className="mb-8" data-testid="progress-container">
          <div className="flex justify-between text-sm text-slate-500 mb-2">
            <span>Step {currentStep} of {totalSteps}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2 bg-slate-100" />
        </div>

        {/* Main Card */}
        <motion.div
          className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <AnimatePresence mode="wait">
            {/* Step 1: Agent Type Selection */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="p-4 sm:p-8 md:p-12"
                data-testid="step-1"
              >
                <div className="text-center mb-8">
                  <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 1</span>
                  <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    What type of partner are you?
                  </h2>
                  <p className="text-slate-600">Select the option that best describes your business</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
                  {agentTypes.map((agent, index) => {
                    const Icon = agent.icon;
                    const isSelected = selectedAgentType?.id === agent.id;
                    
                    return (
                      <motion.div
                        key={agent.id}
                        variants={cardVariants}
                        initial="initial"
                        animate="animate"
                        whileHover="hover"
                        transition={{ duration: 0.2, delay: index * 0.05 }}
                        onClick={() => handleAgentTypeSelect(agent)}
                        className={`
                          relative group cursor-pointer overflow-hidden rounded-xl border-2 bg-white transition-all duration-300
                          ${isSelected 
                            ? 'border-[#10c0a5] ring-2 ring-[#10c0a5] ring-offset-2 bg-[#e6faf7]/30' 
                            : 'border-slate-200 hover:border-[#3dd4c0] hover:shadow-lg'
                          }
                        `}
                        data-testid={`agent-type-${agent.id}`}
                      >
                        <div className="relative h-32 overflow-hidden">
                          <img 
                            src={agent.image} 
                            alt={agent.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                          <div className={`absolute top-3 right-3 p-2 rounded-full ${isSelected ? 'bg-[#10c0a5]' : 'bg-white/90'} transition-colors`}>
                            <Icon className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-slate-700'}`} />
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className={`font-semibold text-lg mb-1 ${isSelected ? 'text-[#10c0a5]' : 'text-slate-900'}`}>
                            {agent.title}
                          </h3>
                          <p className="text-slate-500 text-sm">{agent.description}</p>
                        </div>
                        {isSelected && (
                          <motion.div 
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute top-3 left-3"
                          >
                            <CheckCircle2 className="w-6 h-6 text-[#10c0a5]" />
                          </motion.div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>

                <div className="flex justify-center sm:justify-end">
                  <button
                    onClick={goToStep2}
                    disabled={!selectedAgentType}
                    className={`
                      flex items-center justify-center gap-2 w-full sm:w-auto px-6 sm:px-8 py-3 rounded-lg font-medium transition-all duration-200
                      ${selectedAgentType 
                        ? 'bg-[#10c0a5] hover:bg-[#0ea892] text-white shadow-md hover:shadow-lg hover:-translate-y-0.5' 
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }
                    `}
                    data-testid="continue-to-contact-btn"
                  >
                    <span className="hidden sm:inline">Continue to Contact Info</span>
                    <span className="sm:hidden">Continue</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Contact Information */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="p-4 sm:p-8 md:p-12"
                data-testid="step-2"
              >
                <div className="text-center mb-8">
                  <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 2</span>
                  <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Your Contact Information
                  </h2>
                  <p className="text-slate-600">How can we reach you?</p>
                </div>

                <form onSubmit={contactForm.handleSubmit(handleContactSubmit)} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700 font-medium">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="John Smith"
                      className="h-12 border-slate-200 focus:ring-2 focus:ring-[#10c0a5] focus:ring-offset-2 focus:border-[#10c0a5]"
                      {...contactForm.register('name')}
                      data-testid="input-name"
                    />
                    {contactForm.formState.errors.name && (
                      <p className="text-red-500 text-sm">{contactForm.formState.errors.name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      className="h-12 border-slate-200 focus:ring-2 focus:ring-[#10c0a5] focus:ring-offset-2 focus:border-[#10c0a5]"
                      {...contactForm.register('email')}
                      data-testid="input-email"
                    />
                    {contactForm.formState.errors.email && (
                      <p className="text-red-500 text-sm">{contactForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700 font-medium">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      inputMode="numeric"
                      placeholder="(555) 123-4567"
                      className="h-12 border-slate-200 focus:ring-2 focus:ring-[#10c0a5] focus:ring-offset-2 focus:border-[#10c0a5]"
                      {...contactForm.register('phone')}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        contactForm.setValue('phone', formatted);
                      }}
                      data-testid="input-phone"
                    />
                    {contactForm.formState.errors.phone && (
                      <p className="text-red-500 text-sm">{contactForm.formState.errors.phone.message}</p>
                    )}
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4">
                    <button
                      type="button"
                      onClick={goBack}
                      className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                      data-testid="back-btn-step2"
                    >
                      <ChevronLeft className="w-5 h-5" />
                      Back
                    </button>
                    <button
                      type="submit"
                      className="flex items-center justify-center gap-2 px-6 sm:px-8 py-3 rounded-lg font-medium bg-[#10c0a5] hover:bg-[#0ea892] text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                      data-testid="continue-to-business-btn"
                    >
                      <span className="hidden sm:inline">Continue to Business Info</span>
                      <span className="sm:hidden">Continue</span>
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Step 3: Business Information */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="p-4 sm:p-8 md:p-12"
                data-testid="step-3"
              >
                <div className="text-center mb-8">
                  <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 3</span>
                  <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {categoryQuestions[selectedAgentType?.id]?.title || 'Tell Us About Your Business'}
                  </h2>
                  <p className="text-slate-600">Help us understand your needs better</p>
                </div>

                <form onSubmit={businessForm.handleSubmit(handleBusinessSubmit)} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="company_name" className="text-slate-700 font-medium">Company Name *</Label>
                    <Input
                      id="company_name"
                      placeholder="Your Company Name"
                      className="h-12 border-slate-200 focus:ring-2 focus:ring-[#10c0a5] focus:ring-offset-2 focus:border-[#10c0a5]"
                      {...businessForm.register('company_name')}
                      data-testid="input-company-name"
                    />
                    {businessForm.formState.errors.company_name && (
                      <p className="text-red-500 text-sm">{businessForm.formState.errors.company_name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="website" className="text-slate-700 font-medium">Website</Label>
                    <Input
                      id="website"
                      type="url"
                      placeholder="https://www.yourcompany.com"
                      className="h-12 border-slate-200 focus:ring-2 focus:ring-[#10c0a5] focus:ring-offset-2 focus:border-[#10c0a5]"
                      {...businessForm.register('website')}
                      data-testid="input-website"
                    />
                    {businessForm.formState.errors.website && (
                      <p className="text-red-500 text-sm">{businessForm.formState.errors.website.message}</p>
                    )}
                  </div>

                  {/* Dynamic Category-Specific Questions */}
                  {selectedAgentType && categoryQuestions[selectedAgentType.id]?.questions.map((question, index) => (
                    <div key={question.id} className="space-y-2">
                      <Label htmlFor={question.id} className="text-slate-700 font-medium">{question.label}</Label>
                      
                      {question.type === 'select' && (
                        <Select 
                          onValueChange={(value) => businessForm.setValue(`question_${index + 1}`, value)}
                          data-testid={`select-${question.id}`}
                        >
                          <SelectTrigger className="h-12 border-slate-200 focus:ring-2 focus:ring-[#10c0a5]">
                            <SelectValue placeholder="Select an option" />
                          </SelectTrigger>
                          <SelectContent>
                            {question.options.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      
                      {question.type === 'input' && (
                        <Input
                          id={question.id}
                          placeholder={question.placeholder}
                          className="h-12 border-slate-200 focus:ring-2 focus:ring-[#10c0a5] focus:ring-offset-2 focus:border-[#10c0a5]"
                          {...businessForm.register(`question_${index + 1}`)}
                          data-testid={`input-${question.id}`}
                        />
                      )}
                      
                      {question.type === 'textarea' && (
                        <Textarea
                          id={question.id}
                          placeholder={question.placeholder}
                          className="min-h-[80px] border-slate-200 focus:ring-2 focus:ring-[#10c0a5] focus:ring-offset-2 focus:border-[#10c0a5]"
                          {...businessForm.register(`question_${index + 1}`)}
                          data-testid={`textarea-${question.id}`}
                        />
                      )}
                    </div>
                  ))}

                  <div className="space-y-2">
                    <Label htmlFor="comments" className="text-slate-700 font-medium">Additional Comments</Label>
                    <Textarea
                      id="comments"
                      placeholder="Tell us anything else you'd like us to know..."
                      className="min-h-[100px] border-slate-200 focus:ring-2 focus:ring-[#10c0a5] focus:ring-offset-2 focus:border-[#10c0a5]"
                      {...businessForm.register('comments')}
                      data-testid="input-comments"
                    />
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4">
                    <button
                      type="button"
                      onClick={goBack}
                      className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                      data-testid="back-btn-step3"
                    >
                      <ChevronLeft className="w-5 h-5" />
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex items-center justify-center gap-2 px-6 sm:px-8 py-3 rounded-lg font-medium bg-[#10c0a5] hover:bg-[#0ea892] text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="submit-application-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <span className="hidden sm:inline">Complete Partnership Application</span>
                          <span className="sm:hidden">Submit Application</span>
                          <ChevronRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Step 4: Success */}
            {currentStep === 4 && submissionSuccess && (
              <motion.div
                key="step4"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="p-4 sm:p-8 md:p-12"
                data-testid="step-4-success"
              >
                {/* Success Hero Image */}
                <div className="relative h-48 md:h-64 -mx-8 md:-mx-12 -mt-8 md:-mt-12 mb-8 overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                    alt="Partnership Success"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent" />
                </div>

                <div className="text-center">
                  {/* Success Icon */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                    className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-teal-100 mb-6"
                  >
                    <CheckCircle2 className="w-10 h-10 text-[#10c0a5]" />
                  </motion.div>

                  <motion.h2
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-2xl md:text-3xl font-bold text-slate-900 mb-4"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    Application Submitted Successfully!
                  </motion.h2>

                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-slate-600 text-lg mb-8 max-w-md mx-auto"
                  >
                    Thank you for your interest in partnering with Quoteplicity. 
                    Our team will review your application and get back to you within 1-2 business days.
                  </motion.p>

                  {/* What's Next Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-slate-50 rounded-xl p-6 mb-8 text-left max-w-md mx-auto"
                  >
                    <h3 className="font-semibold text-slate-900 mb-3">What happens next?</h3>
                    <ul className="space-y-2 text-slate-600 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                        <span>We'll review your application within 24-48 hours</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                        <span>A partnership specialist will reach out to discuss next steps</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                        <span>Get access to our quoting software and start growing your business</span>
                      </li>
                    </ul>
                  </motion.div>

                  {/* CTA Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <a
                      href="https://www.quoteplicity.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-medium bg-[#10c0a5] hover:bg-[#0ea892] text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                      data-testid="visit-website-btn"
                    >
                      Visit Quoteplicity
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-8 text-slate-500 text-sm"
        >
          <p>
            Have questions? Contact us at{' '}
            <a href="mailto:partners@quoteplicity.com" className="text-[#10c0a5] hover:text-[#10c0a5] font-medium">
              partners@quoteplicity.com
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PartnerWidget;
